import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { baseurImage } from "../Baseurl";
import { useSelector } from "react-redux";
import SignatureCanvas from "react-signature-canvas";
import axiosInstance from "../api/axiosInstance";
import Swal from "sweetalert2";

export default function MyProfile() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);

  const [avatarVersion, setAvatarVersion] = useState(Date.now());
  const sigCanvasRef = useRef(null);
  const [existingSignature, setExistingSignature] = useState(null);
  const [existingStamp, setExistingStamp] = useState(null);
  const [stampFile, setStampFile] = useState(null);
  const [clearExistingSignature, setClearExistingSignature] = useState(false);
  const [sigSaving, setSigSaving] = useState(false);
  const [userData, setUserData] = useState({
    fullName: "",
    shortName: "",
    email: "",
    phoneNumber: "",
    prefix: "",
    dateOfBirth: "",
    licenseId: "",
    civilId: "",
    passport: "",
    gender: "",
    role: "",
    personalPhoto: "",
    country: "",
    state: "",
  });

  //Load Redux user into local state
  const loadUserData = () => {
    if (!user) return;
    setUserData({
      fullName: user.fullName || "",
      shortName: user.shortName || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      prefix: user.prefix || "",
      dateOfBirth: user.dateOfBirth
        ? new Date(user.dateOfBirth).toLocaleDateString()
        : "",
      licenseId: user.licenseId || "",
      civilId: user.civilId || "",
      passport: user.passport || "",
      gender: user.gender || "",
      role: user.role || "",
      personalPhoto: user.personalPhoto || "",
      country: user.country || "",
      state: user.state || "",
    });
    setAvatarVersion(Date.now()); // bust cache for image refresh
  };


  useEffect(() => {
    loadUserData();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const resolve = (val) => {
      if (!val) return null;
      if (val.startsWith("data:")) return val;
      return `${baseurImage}${val}`;
    };
    setExistingSignature(resolve(user.signature || null));
    setExistingStamp(resolve(user.stamp || null));
  }, [user]);

  const handleSaveSignatureStamp = async () => {
    try {
      setSigSaving(true);
      const doctorId = user?.id;
      if (!doctorId) return;

      const formData = new FormData();
      if (stampFile) formData.append("stamp", stampFile);
      if (clearExistingSignature) {
        formData.append("clearSignature", "true");
      } else if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
        formData.append("signature", sigCanvasRef.current.toDataURL("image/png"));
      }

      await axiosInstance.put(`updateDoctorDetails/${doctorId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Update displayed sig/stamp from what we just saved
      const resolve = (val) => {
        if (!val) return null;
        if (val.startsWith("data:")) return val;
        return `${baseurImage}${val}`;
      };
      if (clearExistingSignature) {
        setExistingSignature(null);
      } else if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
        setExistingSignature(sigCanvasRef.current.toDataURL("image/png"));
      }
      if (stampFile) {
        setExistingStamp(URL.createObjectURL(stampFile));
      }

      setStampFile(null);
      setClearExistingSignature(false);
      sigCanvasRef.current?.clear();
      Swal.fire("Saved", "Signature & Stamp updated successfully.", "success");
    } catch (_) {
      Swal.fire("Error", "Failed to save. Please try again.", "error");
    } finally {
      setSigSaving(false);
    }
  };

 
  useEffect(() => {
    const handleProfileUpdated = () => {
      loadUserData();
      setAvatarVersion(Date.now());
    };
    window.addEventListener("profile-updated", handleProfileUpdated);
    return () => window.removeEventListener("profile-updated", handleProfileUpdated);
  }, []);

  
  useEffect(() => {
    const handleFocus = () => {
      loadUserData();
      setAvatarVersion(Date.now());
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  
 const getProfileImg = () => {
  const photo = userData?.personalPhoto;
  const bustKey = localStorage.getItem("profilePhotoBust") || avatarVersion;
  const bust = `?v=${bustKey}`;

  if (!photo) return "/assets/images/user/avatar-2.jpg";

  if (photo instanceof Blob) {
    const url = URL.createObjectURL(photo);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return url;
  }

  if (typeof photo === "string" && (photo.startsWith("http://") || photo.startsWith("https://"))) {
    return `${photo}${bust}`;
  }

  return `${baseurImage}${photo.replace(/^\/+/, "")}${bust}`;
};

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="row">
          <div className="col-lg-12" style={{ position: "relative" }}>
            {/* Profile Photo */}
            <div className="myProfileImg">
              <img src={getProfileImg()} alt="avatar" />
            </div>

            {/* Profile Info */}
            <div className="cardBorder table-card" style={{ marginTop: "85px" }}>
              <div className="tableHeader d-flex align-items-center justify-content-between">
                <h5 className="mb-0">
                  Profile Details{" "}
                  <i
                    type="button"
                    onClick={() => navigate("/Admin/EditProfile")}
                    className="ti ti-edit f-20"
                    style={{ color: "rgb(70, 128, 255)", cursor: "pointer" }}
                  ></i>
                </h5>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="bgBtn"
                    onClick={() => navigate("/Admin/Changepassword")}
                  >
                    Change Password
                  </button>
                </div>
              </div>

              {/* User Info Section */}
              <div className="p20">
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Full Name</label>
                    <input type="text" className="form-control" value={userData.fullName} disabled />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Email Address</label>
                    <input type="email" className="form-control" value={userData.email} disabled />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Phone Number</label>
                    <input type="text" className="form-control" value={userData.phoneNumber} disabled />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Date of Birth</label>
                    <input type="text" className="form-control" value={userData.dateOfBirth} disabled />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Gender</label>
                    <input type="text" className="form-control" value={userData.gender} disabled />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Role</label>
                    <input type="text" className="form-control" value={userData.role} disabled />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Signature & Stamp Card */}
          <div className="cardBorder table-card" style={{ marginTop: "20px" }}>
            <div className="tableHeader">
              <h5 className="mb-0">Signature &amp; Stamp</h5>
            </div>
            <div className="p20">
              <div className="row g-4">

                {/* Signature */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Signature</label>
                  {existingSignature && !clearExistingSignature && (
                    <div style={{ marginBottom: "10px" }}>
                      <img
                        src={existingSignature}
                        alt="Current signature"
                        style={{ maxHeight: "80px", maxWidth: "100%", border: "1px solid #dee2e6", borderRadius: "6px", background: "#fff", padding: "4px" }}
                      />
                      <div className="mt-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setClearExistingSignature(true)}
                        >
                          Remove &amp; Redraw
                        </button>
                      </div>
                    </div>
                  )}
                  {(!existingSignature || clearExistingSignature) && (
                    <>
                      <div style={{ border: "1px solid #dee2e6", borderRadius: "6px", display: "inline-block", background: "#fff" }}>
                        <SignatureCanvas
                          ref={sigCanvasRef}
                          penColor="#000"
                          canvasProps={{ width: 380, height: 130, style: { display: "block" } }}
                        />
                      </div>
                      <div className="mt-1 d-flex gap-2">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => sigCanvasRef.current?.clear()}>
                          Clear
                        </button>
                        {clearExistingSignature && (
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setClearExistingSignature(false)}>
                            Keep Existing
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Stamp */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Stamp <span className="text-muted" style={{ fontSize: "11px" }}>(PNG / JPG)</span></label>
                  {existingStamp && !stampFile && (
                    <div style={{ marginBottom: "10px" }}>
                      <img
                        src={existingStamp}
                        alt="Current stamp"
                        style={{ maxHeight: "90px", maxWidth: "100%", border: "1px solid #dee2e6", borderRadius: "6px" }}
                      />
                      <div className="text-muted mt-1" style={{ fontSize: "11px" }}>Upload a new image to replace</div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    className="form-control"
                    onChange={(e) => setStampFile(e.target.files[0] || null)}
                  />
                  {stampFile && (
                    <img
                      src={URL.createObjectURL(stampFile)}
                      alt="New stamp"
                      style={{ marginTop: "8px", maxHeight: "90px", maxWidth: "100%", border: "1px solid #dee2e6", borderRadius: "6px" }}
                    />
                  )}
                </div>

                {/* Save Button */}
                <div className="col-12">
                  <button
                    type="button"
                    className="bgBtn"
                    onClick={handleSaveSignatureStamp}
                    disabled={sigSaving}
                  >
                    {sigSaving ? "Saving..." : "Save Signature & Stamp"}
                  </button>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
